import {
  COOKIE_CONSENT_MAX_AGE_MS,
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_STORAGE_SCHEMA,
  CURRENT_COOKIE_POLICY_VERSION,
  DEFAULT_CATEGORIES,
} from './constants';
import { CookieCategory, type CookieCategoryMap, type CookieConsent } from './types';

interface StoredEnvelope {
  schema: number;
  consent: CookieConsent;
}

/** Genera l'id di consenso, con fallback se crypto.randomUUID non disponibile. */
function newConsentId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fallthrough */
  }
  return `c_${Date.now().toString(36)}${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/** Legge il record dal localStorage. Storage corrotto/bloccato → null (=nessuna scelta). */
export function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return migrate(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Persiste il record. In modalità privata/quota piena fallisce in silenzio. */
export function writeConsent(consent: CookieConsent): void {
  try {
    const env: StoredEnvelope = { schema: COOKIE_CONSENT_STORAGE_SCHEMA, consent };
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(env));
  } catch {
    /* lo stato in memoria continua comunque a guidare la UI nella sessione */
  }
}

/** Costruisce un nuovo record forzando NECESSARY a true. */
export function buildConsent(categories: CookieCategoryMap): CookieConsent {
  return {
    id: newConsentId(),
    version: CURRENT_COOKIE_POLICY_VERSION,
    categories: { ...categories, [CookieCategory.NECESSARY]: true },
    timestamp: new Date().toISOString(),
  };
}

/** Va ri-richiesto il consenso? Nessun record OR versione cambiata OR > 6 mesi. */
export function needsReconsent(consent: CookieConsent | null): boolean {
  if (!consent) return true;
  if (consent.version !== CURRENT_COOKIE_POLICY_VERSION) return true;
  const age = Date.now() - new Date(consent.timestamp).getTime();
  return Number.isNaN(age) || age > COOKIE_CONSENT_MAX_AGE_MS;
}

/** Normalizza un envelope sconosciuto/legacy; backfilla categorie nuove al default. */
function migrate(env: unknown): CookieConsent | null {
  if (!env || typeof env !== 'object') return null;
  const e = env as Partial<StoredEnvelope>;
  if (e.schema !== COOKIE_CONSENT_STORAGE_SCHEMA || !e.consent) return null;
  return {
    ...e.consent,
    categories: {
      ...DEFAULT_CATEGORIES,
      ...e.consent.categories,
      [CookieCategory.NECESSARY]: true,
    },
  };
}
